import { Fragment, useCallback, useMemo, useState } from 'react'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import ITHeaderText, { ITHeaderTextCustomer, ITHeaderTextSupplier } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { notHaveCountersWorkingCenter } from '../../../../../../../utils/globals/order/order'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { parseAddressToOneLine } from '../../../../../../../utils/globals/address'
import { getActiveCurrenciesDictionary } from '../../../../../../../utils/reducers/getters'
import { WorkingCenterPayload } from '../../../../../../../utils/reducers/company/workingCenters'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { SetCompleteDataTy } from '../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { BuyTagsPayload } from '../../../../../../../utils/reducers/buys/buyTags'
import { BuyOrderModelType } from '@teinor/erp/types/company/order/buy/buyOrder'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { sortEmployeesByUser } from '../../../../../../../utils/globals/employees'
import { OrderHeaderURLs } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ITHeaderOrderTags } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderOrderTags'
import ModalEditOrderWorkingCenter from '../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditOrderWorkingCenter'
import { UseEditDeleteModalProps } from '../../../../../../../utils/hooks/useEditDeleteModal'
import { CustomSelectBlockedItemsSearchbar } from '../../../../../../../forms/CustomSelect/CustomSelectBlockedItems'
import { InputWithLabelMargin } from '../../../../../../../forms/Input'

type BuyStepHeaderMainProps = InputTableLeftTabsProps & BuyStepHeaderMainLogicProps & {
    employees: EmployeesPayload
    orderHeaderURLs: OrderHeaderURLs
    buyTags: BuyTagsPayload
}

const BuyStepHeaderMain: React.FC<BuyStepHeaderMainProps> = ({ employees, workingCenters, getCompleteData, handleChange, headerData, buyTags,  ...logic }) => {
    const { t } = useTwinTranslation()
    const { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress, openModal, setOpenModal, memorizedWorkingCenters, changeWorkingCenterId } = useBuyStepHeaderMainLogic({ headerData, workingCenters, handleChange, ...logic})
    const extraCustomer: FormElement<keyof BuyOrderModelType>[] = []
    if(headerData?.CustomerId){
        extraCustomer.push(
            {
                name: 'CustomerId',
                component: 'Custom',
                render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
            },
        )
      }
    const sections: FormRender<BuyOrderModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'SupplierId',
                    component: 'Custom',
                    render: () => <ITHeaderTextSupplier label={t('supplier', 'Proveedor')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                ...extraCustomer,
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('representative', 'Representante'),
                    items: employees || {},
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(5),
                    sortItems: true,
                    fieldName: 'fullname_short',
                    sortFunction: (emp) => sortEmployeesByUser(emp, false)
                },
                {
                    name: 'processed_date',
                    component: 'InputCalendarStateFull',
                    label: t('date', 'Fecha'),
                    onlyValids: true,
                    className: returnClassOfSpanTemplate(3),
                    required: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value } ) => <ITHeaderText label={t('draftN', 'Nº de borrador')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'supplier_number_order',
                    component: 'InputWithLabelMargin',
                    label: t('supplierNumberOrder', 'Num. pedido proveedor'),
                    className: returnClassOfSpanTemplate(5),
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'InputWithLabelMargin',
                    label: t('supplierInvoice', 'Factura del proveedor'),
                    className: returnClassOfSpanTemplate(5),
                },
                {
                    name: 'supplier_date',
                    component: 'InputCalendarStateFull',
                    label: t('dateSupplier', 'Fecha del proveedor'),
                    onlyValids: true,
                    className: returnClassOfSpanTemplate(5),
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectBlockedItemsSearchbar label={t('requestedBy', 'Pedido por')} items={extraProps.memorizedWorkingCenters} value={extraProps.WorkingCenterId} onChange={extraProps.changeWorkingCenterId} className={returnClassOfSpanTemplate(3)} isStateLess={true} blockItemsFnc={extraProps.notHaveCountersWorkingCenter} sortItems={true} />
                    },
                    extraProps: {
                        memorizedWorkingCenters,
                        WorkingCenterId: headerData.WorkingCenterId,
                        changeWorkingCenterId,
                        notHaveCountersWorkingCenter
                    }
                },
                {
                    name: 'WarehouseId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('destinationWarehouse', 'Almacén destino'),
                    items: dictWarehouses,
                    className: returnClassOfSpanTemplate(3),
                    sortItems: true
                },
                {
                    name: 'SupplierAddress',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('addressSupplier', 'Dirección proveedor')} value={parseAddressToOneLine(headerData.Supplier)} className={returnClassOfSpanTemplate(5)} />
                    },
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: { workingCenterAddress }
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectWithSearchBar label={t('currency', 'Moneda')} items={extraProps.activeCurrencies} value={extraProps.CurrencyId} onChange={extraProps.changeCurrencyId} className={returnClassOfSpanTemplate(2)} isStateLess={true} />
                    },
                    extraProps: {
                        activeCurrencies,
                        CurrencyId: headerData.CurrencyId,
                        changeCurrencyId
                    }
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={buyTags} headerData={headerData} onChange={handleChange} {...logic} className={returnClassOfSpanTemplate(5)} />)
                    },
                    extraProps: { selectedTags: headerData?.tags }
                },
            ]
        }
    ]
    return (
        <Fragment>
            <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
            {openModal?.type === 'edit' && openModal?.allRowData?.WorkingCenterId ?
                <ModalEditOrderWorkingCenter id={headerData?.id} {...{ updateWorkingCenterURL: logic.orderHeaderURLs?.updateWorkingCenter, onSubmit: getCompleteData, setOpened: () => setOpenModal(null), allRowData: openModal?.allRowData }} />
                : null
            }
        </Fragment>
    )
}

interface BuyStepHeaderMainLogicProps {
    headerData: TwinDictionary
    workingCenters: WorkingCenterPayload
    setCompleteData: SetCompleteDataTy
    handleChange: InputTableLeftColumnChange
}

export const useBuyStepHeaderMainLogic = ({ workingCenters, headerData, setCompleteData, handleChange }: BuyStepHeaderMainLogicProps) => {
    const WorkingCenterId = headerData.WorkingCenterId
    const CurrencyId = headerData.CurrencyId
    const activeCurrencies = getActiveCurrenciesDictionary()
    const dictWarehouses = useMemo(() => dictionaryComplexFromJsonArr(workingCenters?.[parseInt(WorkingCenterId || '1')]?.Warehouses || []), [workingCenters, WorkingCenterId])
    const WorkingCenter = headerData.BillingWorkingCenter
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(WorkingCenter), [WorkingCenter])
    const [openModal, setOpenModal] = useState<UseEditDeleteModalProps>(null)
    const memorizedWorkingCenters = useMemo(() => {
        const copyWorkingCenters = JSON.parse(JSON.stringify(workingCenters))
        if (headerData.WorkingCenterId && headerData.WorkingCenter && !copyWorkingCenters[headerData.WorkingCenterId]) {
            copyWorkingCenters[headerData.WorkingCenterId] = { id: headerData.WorkingCenterId, name: headerData.WorkingCenter?.name }
        }
        return copyWorkingCenters
    }, [headerData?.WorkingCenterId, headerData?.WorkingCenter, workingCenters])
    
    const changeCurrencyId = useCallback((value: string) => {
        if (value !== CurrencyId){
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.header.CurrencyId = value
                return { ...copyOld }
            })
            handleChange(headerData?.id, 'CurrencyId', value)
        }
    }, [CurrencyId, handleChange, headerData?.id, setCompleteData])

    const changeWorkingCenterId = useCallback((value: string) => {
        if (value !== CurrencyId) {
            setOpenModal({ type: 'edit', allRowData: { WorkingCenterId: value } })
        }
    }, [setOpenModal, CurrencyId])

    return { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress, openModal, setOpenModal, memorizedWorkingCenters, changeWorkingCenterId }
}



export default BuyStepHeaderMain